import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, CheckCircle, FileText, Plus, Save, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const emptyLine = () => ({ description: '', details: '', quantity: 1, unit: 'Stk.', unit_price: 0, discount_pct: 0, skonto_pct: 0 });
const num = (value) => parseFloat(value) || 0;
const money = (value) => `CHF ${(Number(value) || 0).toFixed(2)}`;
const toDateInput = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

function computeTotals(lines, mwstRate = 8.1) {
  const subtotal = (lines || []).reduce((sum, line) => {
    const quantity = num(line.quantity) || 1;
    const unitPrice = num(line.unit_price);
    const discount = num(line.discount_pct);
    const skonto = num(line.skonto_pct);
    return sum + quantity * unitPrice * (1 - discount / 100) * (1 - skonto / 100);
  }, 0);
  const mwst = subtotal * (mwstRate / 100);
  return { subtotal, mwst, total: subtotal + mwst };
}

function buildPayload(form) {
  return {
    ...form,
    cost_limit: form.cost_limit ? num(form.cost_limit) : null,
    invoice_lines: (form.invoice_lines || []).map(line => ({
      ...line,
      quantity: num(line.quantity) || 1,
      unit_price: num(line.unit_price),
      discount_pct: num(line.discount_pct),
      skonto_pct: num(line.skonto_pct),
    })),
  };
}

function DocumentEditor({ type }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isOrder = type === 'order';
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ mwst_rate: 8.1 });

  useEffect(() => {
    Promise.all([
      api.get(isOrder ? `/orders/${id}` : `/quotes/${id}`),
      api.get('/admin/settings').catch(() => ({ data: { mwst_rate: 8.1 } })),
    ])
      .then(([doc, cfg]) => {
        setForm({ ...doc.data, invoice_lines: doc.data.invoice_lines || [] });
        setSettings(cfg.data || { mwst_rate: 8.1 });
      })
      .catch(() => toast.error(isOrder ? 'Rechnung konnte nicht geladen werden' : 'Offerte konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [id, isOrder]);

  const totals = useMemo(() => computeTotals(form?.invoice_lines || [], settings.mwst_rate || 8.1), [form, settings]);

  const setField = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const setLine = (idx, key, value) => setForm(current => ({
    ...current,
    invoice_lines: current.invoice_lines.map((line, i) => i === idx ? { ...line, [key]: value } : line),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(isOrder ? `/orders/${id}` : `/quotes/${id}`, buildPayload(form));
      setForm({ ...res.data, invoice_lines: res.data.invoice_lines || [] });
      toast.success('Gespeichert');
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const openPdf = () => {
    const token = localStorage.getItem('yh_token');
    const base = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const url = `${base}/api/${isOrder ? 'orders' : 'quotes'}/${id}/pdf${isOrder ? '?qr=true' : ''}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => window.open(URL.createObjectURL(blob), '_blank'))
      .catch(() => toast.error('PDF konnte nicht geöffnet werden'));
  };

  const markPaid = async () => {
    try {
      const res = await api.patch(`/orders/${id}/invoice-status`, { status: 'paid' });
      setForm({ ...res.data, invoice_lines: res.data.invoice_lines || [] });
      toast.success('Als bezahlt markiert');
    } catch {
      toast.error('Status konnte nicht geändert werden');
    }
  };

  if (loading || !form) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const displayNumber = isOrder ? (form.invoice_number || form.order_number) : form.quote_number;
  const title = isOrder ? 'Rechnung' : 'Offerte';
  const backUrl = isOrder ? `/orders/${id}` : `/quotes/${id}`;
  const defaultIntroText = [
    form.customer_name ? `Verwaltung: ${form.customer_name}` : '',
    form.owner_name ? `Eigentümer: ${form.owner_name}` : '',
    form.property_address ? `Liegenschaft: ${form.property_address}` : '',
    [form.object_name, form.renter_name].filter(Boolean).join(', '),
    '',
    isOrder ? 'Wir bedanken uns für Ihren Auftrag und stellen folgende Positionen in Rechnung:' : 'Wir bedanken uns für Ihre Anfrage und unterbreiten Ihnen folgende Offerte:',
  ].filter(Boolean).join('\n');
  const introText = isOrder ? (form.invoice_intro_text ?? defaultIntroText) : defaultIntroText;

  return (
    <Layout>
      <div className="p-6 md:p-8 bg-muted/30 min-h-full" data-testid={`${type}-document-editor`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(backUrl)} className="p-2 text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary" strokeWidth={1.5} />
                <h1 className="font-heading font-black text-3xl">{title}</h1>
                <span className="text-muted-foreground">/</span>
                <span className="text-sm text-muted-foreground">{displayNumber}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate(backUrl)} className="rounded-sm">Zurück</Button>
              <Button variant="outline" onClick={openPdf} className="rounded-sm gap-2">
                <FileText className="w-4 h-4" /> PDF
              </Button>
              {isOrder && form.invoice_status !== 'paid' && (
                <Button variant="outline" onClick={markPaid} className="rounded-sm gap-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                  <CheckCircle className="w-4 h-4" /> Bezahlt
                </Button>
              )}
              <Button onClick={save} disabled={saving} className="rounded-sm gap-2">
                <Save className="w-4 h-4" /> {saving ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
          </div>

          <div className="bg-card border border-border/60 rounded-sm p-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-6 border-b border-border/60">
              <div className="lg:col-span-5 space-y-4">
                <div className="space-y-1.5">
                  <Label>{isOrder ? 'Kunde / Verwaltung' : 'Kunde / Verwaltung'}</Label>
                  <Input value={form.customer_name || ''} onChange={e => setField('customer_name', e.target.value)} className="rounded-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label>Rechnungsadresse</Label>
                  <Textarea value={form.customer_address || ''} onChange={e => setField('customer_address', e.target.value)} rows={3} className="rounded-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label>{isOrder ? 'Rechnungsnummer' : 'Offertennummer'}</Label>
                  <Input
                    value={displayNumber || ''}
                    disabled={!isOrder}
                    onChange={e => isOrder && setField('invoice_number', e.target.value)}
                    className={`rounded-sm ${!isOrder ? 'bg-muted' : ''}`}
                  />
                </div>
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4 content-start">
                <div className="space-y-1.5">
                  <Label>Ausstellungsdatum</Label>
                  <Input
                    type={isOrder ? 'date' : 'text'}
                    value={isOrder ? (form.invoice_issue_date || toDateInput(form.created_at)) : new Date(form.created_at || Date.now()).toLocaleDateString('de-CH')}
                    disabled={!isOrder}
                    onChange={e => isOrder && setField('invoice_issue_date', e.target.value)}
                    className={`rounded-sm ${!isOrder ? 'bg-muted' : ''}`}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Lieferdatum</Label>
                  <div className="relative">
                    <Input type="date" value={form.deadline || ''} onChange={e => setField('deadline', e.target.value)} className="rounded-sm pr-9" />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" strokeWidth={1.5} />
                  </div>
                </div>
                {isOrder && (
                  <>
                    <div className="space-y-1.5">
                      <Label>Zahlungsziel</Label>
                      <Select value="custom">
                        <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="custom">Manuell</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Zahlbar bis</Label>
                      <Input type="date" value={form.invoice_due_date || ''} onChange={e => setField('invoice_due_date', e.target.value)} className="rounded-sm" />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="py-6 border-b border-border/60">
              <Textarea
                value={introText}
                onChange={e => isOrder && setField('invoice_intro_text', e.target.value)}
                readOnly={!isOrder}
                rows={6}
                className={`rounded-sm text-sm ${isOrder ? '' : 'bg-muted/60'}`}
              />
            </div>

            <div className="py-6">
              <div className="grid grid-cols-[minmax(220px,1fr)_88px_88px_120px_110px_120px_36px] gap-2 text-xs text-muted-foreground mb-2 px-1">
                <span>Artikel</span><span>Menge</span><span>Einheit</span><span>Einzelpreis</span><span>Rabatt (%)</span><span>Gesamtbetrag</span><span />
              </div>
              <div className="space-y-3">
                {form.invoice_lines.map((line, idx) => {
                  const lineTotal = (num(line.quantity) || 1) * num(line.unit_price) * (1 - num(line.discount_pct) / 100) * (1 - num(line.skonto_pct) / 100);
                  return (
                    <div key={idx} className="space-y-2">
                      <div className="grid grid-cols-[minmax(220px,1fr)_88px_88px_120px_110px_120px_36px] gap-2 items-start">
                        <Input value={line.description} onChange={e => setLine(idx, 'description', e.target.value)} placeholder="Position" className="rounded-sm font-medium" />
                        <Input type="number" value={line.quantity} onChange={e => setLine(idx, 'quantity', e.target.value)} className="rounded-sm text-right" />
                        <Input value={line.unit} onChange={e => setLine(idx, 'unit', e.target.value)} className="rounded-sm text-center" />
                        <Input type="number" value={line.unit_price} onChange={e => setLine(idx, 'unit_price', e.target.value)} className="rounded-sm text-right" />
                        <Input type="number" value={line.discount_pct} onChange={e => setLine(idx, 'discount_pct', e.target.value)} className="rounded-sm text-right" />
                        <Input value={lineTotal.toFixed(2)} disabled className="rounded-sm text-right bg-muted" />
                        <button onClick={() => setForm(current => ({ ...current, invoice_lines: current.invoice_lines.filter((_, i) => i !== idx) }))} className="h-10 w-9 flex items-center justify-center text-muted-foreground hover:text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <Textarea value={line.details || ''} onChange={e => setLine(idx, 'details', e.target.value)} placeholder="Eine Beschreibung hier eingeben" rows={2} className="rounded-sm w-full md:w-[320px] resize-y" />
                    </div>
                  );
                })}
              </div>
              <Button variant="outline" onClick={() => setForm(current => ({ ...current, invoice_lines: [...current.invoice_lines, emptyLine()] }))} className="rounded-sm mt-4 gap-2">
                <Plus className="w-4 h-4" /> Artikel hinzufügen
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-2">
              <Textarea
                value={form.notes || ''}
                onChange={e => setField('notes', e.target.value)}
                placeholder="Bemerkung / Danke"
                rows={5}
                className="lg:col-span-8 rounded-sm"
              />
              <div className="lg:col-span-4">
                <div className="border border-border/60 rounded-sm overflow-hidden">
                  <div className="flex justify-between px-4 py-3 text-sm">
                    <span>NETTOBETRAG</span><span>{money(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between px-4 py-3 text-sm text-muted-foreground border-t border-border/60">
                    <span>MWST {Number(settings.mwst_rate || 8.1).toFixed(1)}%</span><span>{money(totals.mwst)}</span>
                  </div>
                  <div className="flex justify-between px-4 py-4 bg-[#4b2033] text-white font-bold">
                    <span>GESAMTBETRAG</span><span>{money(totals.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export function InvoiceEditorPage() {
  return <DocumentEditor type="order" />;
}

export function QuoteDocumentEditorPage() {
  return <DocumentEditor type="quote" />;
}
